// src/pages/BookingsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Calendar, Users, Clock, X } from "lucide-react";
import { TopBar, EmptyState, ErrorMsg } from "../components/UI";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
  pending:   { bg: "rgba(122,106,85,0.12)", color: "#7A6A55", label: "Pending" },
  approved:  { bg: "rgba(74,112,82,0.12)",  color: "#4A7052", label: "Confirmed" },
  rejected:  { bg: "rgba(107,39,55,0.12)",  color: "#886070", label: "Declined" },
  cancelled: { bg: "rgba(90,90,90,0.12)",   color: "#7A7A7A", label: "Cancelled" },
};

// Mirrors ToursPage's TourItem layout exactly (same card shell, same
// absolute-positioned corner action, same meta-row icon pattern) so My
// Tours and My Bookings read as the same app instead of two designs.
function BookingItem({ booking, onCancel, cancelling }) {
  const s = STATUS_STYLE[booking.status] || STATUS_STYLE.pending;
  const canCancel = booking.status === "pending" || booking.status === "approved";
  return (
    <div className="card" style={{ position: "relative" }}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 100 }}>
            {s.label}
          </span>
        </div>
        <p className="font-display font-semibold text-slate-dark">
          {booking.winery_name}
        </p>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-stone">
          <span className="flex items-center gap-1">
            <Calendar size={11} /> {booking.visit_date}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={11} /> {String(booking.slot_time).slice(0, 5)}
          </span>
          <span className="flex items-center gap-1">
            <Users size={11} /> {booking.party_size} {booking.party_size === 1 ? "person" : "people"}
          </span>
        </div>
        {booking.status === "rejected" && booking.manager_notes && (
          <p className="text-xs text-stone mt-2">Note: {booking.manager_notes}</p>
        )}
      </div>

      {/* Reserves the same corner every card uses (whether or not this one
          is cancellable) so cards don't visually jump between states —
          same spot ToursPage's delete control always occupies. */}
      {canCancel ? (
        <button onClick={onCancel} disabled={cancelling}
          aria-label="Cancel booking" title="Cancel booking"
          style={{
            position: "absolute", top: 12, right: 12,
            background: "none", border: "none", color: "#A8907E",
            cursor: cancelling ? "default" : "pointer", padding: 4,
            opacity: cancelling ? 0.5 : 1,
          }}>
          <X size={14} />
        </button>
      ) : null}
    </div>
  );
}

export default function BookingsPage() {
  const { user }     = useAuth("traveler");
  const navigate     = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [cancellingId, setCancellingId] = useState(null);

  const signedIn = user && user.role === "traveler";

  useEffect(() => {
    if (!signedIn) { setLoading(false); return; }
    api.myBookings()
      .then(setBookings)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [signedIn]);

  async function handleCancel(bookingId) {
    setCancellingId(bookingId);
    try {
      await api.cancelBooking(bookingId);
      setBookings(bs => bs.map(b => b.id === bookingId ? { ...b, status: "cancelled" } : b));
    } catch (err) {
      setError(err.message);
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className="min-h-screen pb-20">
      <TopBar title="My bookings" />

      <div className="px-4 pt-6">
        {!signedIn && (
          <div className="card text-center py-8">
            <p className="font-display text-lg font-semibold text-slate-dark mb-1">
              Sign in to see your bookings
            </p>
            <p className="text-sm text-stone mb-4">
              Tasting bookings made while signed in (or with a matching email) show up here.
            </p>
            <button onClick={() => navigate("/tourist/login", { state: { from: "/bookings" } })} className="btn-primary">
              Sign in or register
            </button>
          </div>
        )}

        {signedIn && loading && (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-chalk-warm rounded w-1/3 mb-3" />
                <div className="h-5 bg-chalk-warm rounded w-1/2 mb-2" />
                <div className="h-3 bg-chalk-warm rounded w-1/4" />
              </div>
            ))}
          </div>
        )}

        {signedIn && !loading && error && <ErrorMsg message={error} />}

        {signedIn && !loading && !error && bookings.length === 0 && (
          <EmptyState
            icon={CalendarCheck}
            title="No bookings yet"
            body="Book a tasting from any winery on your tour and it'll show up here."
            action={
              <button onClick={() => navigate("/tours")} className="btn-primary">
                Go to My Tours
              </button>
            }
          />
        )}

        {signedIn && !loading && bookings.length > 0 && (
          <div className="flex flex-col gap-3">
            {bookings.map(b => (
              <BookingItem
                key={b.id}
                booking={b}
                cancelling={cancellingId === b.id}
                onCancel={() => handleCancel(b.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
